import { RefObject, useRef, useState } from 'react';
import {
  useCreateCommunicationInput,
  useCreateOfferInput,
  useDeleteOfferInput,
  useUpdateCommunicationInput,
  useUpdateOfferInput,
  useValidateCommunication,
  useVerifyCommunication,
} from '../../../../../hooks/Marketing';
import type { CheckOperationSnapshot } from '../../../../../hooks/Marketing';
import type { ValidationIssue } from '../../../../../utils/checkOperationErrors';
import type { CreationStepHandle } from '../CampaignCreationStep';
import type { EmailUnlayerHandle } from '../EmailUnlayerSection';
import { isRichSms, withEmailContent, withRichSmsContent } from '../creationPayload';
import { hasOfferContent } from '../offers/offerForm';
import { buildOfferBody, toMergePatch } from '../offers/offerPayload';
import { buildCreatePayload, buildUpdatePayload } from './campaignPayload';
import type { CampaignForm } from './useCampaignForm';

interface PersistOptions {
  form: CampaignForm;
  creationStepRef: RefObject<CreationStepHandle | null>;
  richLandingRef: RefObject<EmailUnlayerHandle | null>;
}

export const usePersistCampaign = ({ form, creationStepRef, richLandingRef }: PersistOptions) => {
  const { definition, targeting, offers, creationPayloadRef, staleCreationKeysRef, acknowledgements } = form;
  const [communicationInputId, setCommunicationInputId] = useState<number | null>(form.initialComm?.id ?? null);
  const [saving, setSaving] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const [recap, setRecap] = useState<{ commId: number; snapshot: CheckOperationSnapshot | null } | null>(null);
  const [verifying, setVerifying] = useState(false);
  const creatingRef = useRef(false);
  const persistedOffersRef = useRef(new Map((form.initialComm?.offerInputs ?? []).map(o => [String(o.id), o.id])));

  const { createCommunicationInput, isLoading: creating, error: saveError } = useCreateCommunicationInput();
  const { updateCommunicationInput } = useUpdateCommunicationInput();
  const { createOfferInput } = useCreateOfferInput();
  const { updateOfferInput } = useUpdateOfferInput();
  const { deleteOfferInput } = useDeleteOfferInput();
  const { validateCommunication, isLoading: validating } = useValidateCommunication();
  const { verifyCommunication } = useVerifyCommunication();

  const captureEditorContent = async () => {
    if (definition.channel === 'email') {
      const content = await creationStepRef.current?.getEmailContent();
      if (content) creationPayloadRef.current = withEmailContent(creationPayloadRef.current, content);
    }
    if (definition.channel === 'sms' && isRichSms(creationPayloadRef.current)) {
      const content = await richLandingRef.current?.getContent();
      if (content) creationPayloadRef.current = withRichSmsContent(creationPayloadRef.current, content);
    }
  };

  const createCommunication = async (): Promise<number | null> => {
    if (creatingRef.current) return null;
    creatingRef.current = true;
    try {
      const created = await createCommunicationInput(buildCreatePayload(definition, targeting, creationPayloadRef.current));
      if (created) setCommunicationInputId(created.id);
      return created?.id ?? null;
    } finally {
      creatingRef.current = false;
    }
  };

  const updateCommunication = (id: number) =>
    updateCommunicationInput(id, buildUpdatePayload(definition, targeting, creationPayloadRef.current, staleCreationKeysRef.current));

  const syncOffers = async (commId: number) => {
    const persisted = persistedOffersRef.current;
    const toSave = definition.hasOffer ? offers.filter(hasOfferContent) : [];
    const keptIds = new Set(toSave.map(o => o.id));
    const removed = [...persisted.entries()].filter(([formId]) => !keptIds.has(formId));

    await Promise.all(removed.map(async ([formId, apiId]) => {
      if (await deleteOfferInput(apiId)) persisted.delete(formId);
    }));
    await Promise.all(toSave.map(async offer => {
      const body = buildOfferBody(offer, definition);
      const existingId = persisted.get(offer.id);
      if (existingId !== undefined) {
        await updateOfferInput(existingId, toMergePatch(body));
        return;
      }
      const created = await createOfferInput({ communicationInput: `/api/communication_inputs/${commId}`, ...body });
      if (created?.id) persisted.set(offer.id, created.id);
    }));
  };

  const persist = async (errorMessage: string): Promise<number | null> => {
    if (definition.channel === '' || definition.title.trim() === '') {
      setDraftError('Veuillez saisir un titre et sélectionner un canal avant d\'enregistrer.');
      return null;
    }
    setSaving(true);
    setDraftError('');
    try {
      await captureEditorContent();
      const commId = communicationInputId ?? await createCommunication();
      if (commId === null || (communicationInputId !== null && !(await updateCommunication(commId)))) {
        setDraftError(errorMessage);
        return null;
      }
      await syncOffers(commId);
      form.markClean();
      return commId;
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = () => persist('Erreur lors de l\'enregistrement du brouillon.');

  const showIssues = (outcome: { message: string; issues: ValidationIssue[] }, fallback: string) => {
    if (outcome.issues.length > 0) setValidationIssues(outcome.issues);
    else setDraftError(outcome.message || fallback);
  };

  // Étape 1 de "Terminer" : enregistre puis appelle check-operations/verify pour afficher le récap.
  const terminate = async (): Promise<void> => {
    setValidationIssues([]);
    setVerifying(true);
    try {
      const commId = await persist('Erreur lors de l\'enregistrement de la campagne.');
      if (commId === null) return;
      const outcome = await verifyCommunication(commId);
      if (outcome.ok) setRecap({ commId, snapshot: outcome.snapshot });
      else showIssues(outcome, "Erreur lors de la vérification de l'opération.");
    } finally {
      setVerifying(false);
    }
  };

  // Étape 2 : l'utilisateur confirme le récap, on appelle check-operations/validate.
  const confirmValidation = async (): Promise<boolean> => {
    if (!recap) return false;
    const outcome = await validateCommunication({
      communicationInputId: recap.commId,
      acceptBATVisualization: acknowledgements.bat || definition.channel === 'wallet' ? 1 : 0,
      acceptCGV: acknowledgements.cgv ? 1 : 0,
      needImmediateExtract: 0,
    });
    setRecap(null);
    if (outcome.ok) return true;
    showIssues(outcome, "Erreur lors de la validation de l'opération.");
    return false;
  };

  return {
    communicationInputId,
    isSaved: communicationInputId !== null,
    saving,
    creating,
    saveError,
    draftError,
    clearDraftError: () => setDraftError(''),
    validationIssues,
    clearValidationIssues: () => setValidationIssues([]),
    saveDraft,
    terminate,
    verifying,
    recap,
    closeRecap: () => setRecap(null),
    validating,
    confirmValidation,
  };
};
